import { type ReactElement } from 'react';
import { useTable, List, ShowButton } from '@refinedev/antd';
import type { HttpError, CrudFilters } from '@refinedev/core';
import {
  Table,
  Tag,
  Space,
  Input,
  Form,
  Select,
  List as AntList,
  Card,
  Grid,
  Tooltip,
  Typography,
} from 'antd';

import type { AdminRecoveryEvent, RecoveryKind } from '@/types';

import {
  RECOVERY_KINDS,
  RECOVERY_KIND_COLORS,
  RECOVERY_KIND_LABELS,
} from './recoveryKindMeta';

const { useBreakpoint } = Grid;
const { Text } = Typography;

// ---------------------------------------------------------------------------
// Search form shape
// ---------------------------------------------------------------------------

interface RecoveryEventSearchForm {
  recoveryKind?: RecoveryKind;
  orderId?: string;
  threadId?: string;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function recoveryKindTag(value: RecoveryKind): ReactElement {
  return (
    <Tag color={RECOVERY_KIND_COLORS[value] ?? 'default'}>
      {RECOVERY_KIND_LABELS[value] ?? value}
    </Tag>
  );
}

function pickDetailString(
  details: Record<string, unknown> | null | undefined,
  key: string
): string {
  if (!details) return '—';
  const v = details[key];
  if (v === undefined || v === null) return '—';
  if (typeof v === 'string') return v;
  if (typeof v === 'number' || typeof v === 'boolean') return String(v);
  return JSON.stringify(v);
}

// ---------------------------------------------------------------------------
// Mobile card component
// ---------------------------------------------------------------------------

interface MobileRecoveryEventCardProps {
  record: AdminRecoveryEvent;
}

function MobileRecoveryEventCard({
  record,
}: MobileRecoveryEventCardProps): ReactElement {
  return (
    <Card size="small" style={{ width: '100%' }}>
      <Space direction="vertical" size={4} style={{ width: '100%' }}>
        <div>
          <strong>Kind:</strong> {recoveryKindTag(record.recoveryKind)}
        </div>
        <div>
          <strong>Order:</strong>{' '}
          <a href={`/orders/show/${record.orderId}`}>
            {record.orderId.slice(0, 8)}...
          </a>
        </div>
        <div>
          <strong>Outcome:</strong>{' '}
          {pickDetailString(record.details, 'outcome')}
        </div>
        <div>
          <strong>Created:</strong>{' '}
          {new Date(record.createdAt).toLocaleString()}
        </div>
        <Space size="small">
          <ShowButton hideText size="small" recordItemId={record.id} />
        </Space>
      </Space>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function RecoveryEventList(): ReactElement {
  const screens = useBreakpoint();
  const isMobile = !screens.md;

  const { tableProps, searchFormProps } = useTable<
    AdminRecoveryEvent,
    HttpError,
    RecoveryEventSearchForm
  >({
    resource: 'recovery-events',
    syncWithLocation: true,
    sorters: {
      initial: [{ field: 'createdAt', order: 'desc' }],
    },
    onSearch: (values) => {
      const filters: CrudFilters = [
        {
          field: 'recoveryKind',
          operator: 'eq',
          value: values.recoveryKind,
        },
        {
          field: 'orderId',
          operator: 'eq',
          value: values.orderId,
        },
        {
          field: 'threadId',
          operator: 'eq',
          value: values.threadId,
        },
      ];
      return filters;
    },
  });

  return (
    <List>
      <Space style={{ marginBottom: 16, flexWrap: 'wrap' }}>
        <Form {...searchFormProps} layout="inline">
          <Form.Item name="recoveryKind">
            <Select
              placeholder="Filter by kind"
              allowClear
              style={{ width: 220 }}
              options={RECOVERY_KINDS.map((k) => ({
                label: RECOVERY_KIND_LABELS[k],
                value: k,
              }))}
              onChange={(val: RecoveryKind | undefined) => {
                searchFormProps.form?.setFieldsValue({ recoveryKind: val });
                searchFormProps.form?.submit();
              }}
            />
          </Form.Item>
          <Form.Item name="orderId">
            <Input.Search
              placeholder="Search by Order ID"
              allowClear
              onSearch={(value) => {
                searchFormProps.form?.setFieldsValue({ orderId: value });
                searchFormProps.form?.submit();
              }}
              style={{ width: 240 }}
            />
          </Form.Item>
          <Form.Item name="threadId">
            <Input.Search
              placeholder="Search by Thread ID"
              allowClear
              onSearch={(value) => {
                searchFormProps.form?.setFieldsValue({ threadId: value });
                searchFormProps.form?.submit();
              }}
              style={{ width: 240 }}
            />
          </Form.Item>
        </Form>
      </Space>

      {isMobile ? (
        <AntList
          dataSource={tableProps.dataSource ? [...tableProps.dataSource] : []}
          pagination={
            tableProps.pagination && typeof tableProps.pagination === 'object'
              ? {
                  current: tableProps.pagination.current,
                  pageSize: tableProps.pagination.pageSize,
                  total: tableProps.pagination.total,
                  onChange: tableProps.pagination.onChange,
                }
              : false
          }
          renderItem={(record) => (
            <AntList.Item key={record.id} style={{ padding: '8px 0' }}>
              <MobileRecoveryEventCard record={record} />
            </AntList.Item>
          )}
        />
      ) : null}

      {!isMobile ? (
        <Table {...tableProps} rowKey="id" scroll={{ x: 1100 }} size="small">
          <Table.Column<AdminRecoveryEvent>
            dataIndex="createdAt"
            title="Created"
            render={(value: string) => new Date(value).toLocaleString()}
            width={180}
            sorter
          />

          <Table.Column<AdminRecoveryEvent>
            dataIndex="recoveryKind"
            title="Kind"
            render={(value: RecoveryKind) => recoveryKindTag(value)}
            width={170}
          />

          <Table.Column<AdminRecoveryEvent>
            dataIndex="orderId"
            title="Order ID"
            render={(value: string) => (
              <Tooltip title={value}>
                <a href={`/orders/show/${value}`}>
                  <Text copyable={{ text: value }}>{value.slice(0, 8)}...</Text>
                </a>
              </Tooltip>
            )}
            width={160}
          />

          <Table.Column<AdminRecoveryEvent>
            dataIndex="threadId"
            title="Thread ID"
            render={(value: string) =>
              value ? (
                <Tooltip title={value}>
                  <Text copyable={{ text: value }}>
                    {value.length > 12 ? `${value.slice(0, 12)}...` : value}
                  </Text>
                </Tooltip>
              ) : (
                '—'
              )
            }
            width={160}
          />

          <Table.Column<AdminRecoveryEvent>
            title="Outcome"
            render={(_: unknown, record: AdminRecoveryEvent) =>
              pickDetailString(record.details, 'outcome')
            }
            width={180}
          />

          <Table.Column<AdminRecoveryEvent>
            title="Status"
            render={(_: unknown, record: AdminRecoveryEvent) =>
              pickDetailString(record.details, 'status')
            }
            width={180}
          />

          <Table.Column<AdminRecoveryEvent>
            title="Decision"
            render={(_: unknown, record: AdminRecoveryEvent) =>
              pickDetailString(record.details, 'decision')
            }
            width={120}
          />

          <Table.Column<AdminRecoveryEvent>
            title="Actions"
            fixed="right"
            width={80}
            render={(_: unknown, record: AdminRecoveryEvent) => (
              <Space size="small">
                <ShowButton hideText size="small" recordItemId={record.id} />
              </Space>
            )}
          />
        </Table>
      ) : null}
    </List>
  );
}
